import { fg } from '@atlaskit/platform-feature-flags/fg';

import { type OperationalEventAttributes } from '../../../common/utils/analytics-client';
import { Logger } from '../../../common/utils/logger';

interface SetItemErrorReport {
	message: string;
	attributes?: OperationalEventAttributes;
	// The caught error, used only to classify the failure (e.g. detect quota-exceeded).
	error?: unknown;
}

/**
 * Identifies browser "quota exceeded" storage failures across the cross-browser variants (per MDN's
 * `Storage.setItem` exceptions): modern browsers throw a `DOMException` named `QuotaExceededError`;
 * legacy WebKit/Blink expose the numeric `code === 22` (`QUOTA_EXCEEDED_ERR`); Firefox historically
 * throws `NS_ERROR_DOM_QUOTA_REACHED` with `code === 1014`.
 */
const isQuotaExceededError = (error: unknown): boolean => {
	if (!error || typeof error !== 'object') {
		return false;
	}

	const { name, code } = error as { name?: unknown; code?: unknown };

	return (
		name === 'QuotaExceededError' ||
		name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
		code === 22 ||
		code === 1014
	);
};

/**
 * Reports a web-storage `setItem` failure, deciding whether the failure should feed the operational
 * `usedAtlBrowserStorageSetItemError` metric.
 *
 * Expected, non-actionable browser quota-exceeded failures generate high-volume noise that drives
 * the "Set Item Error" Splunk detector. When the filter gate is ON, a quota-exceeded failure is only
 * logged locally (for debugging) and is kept out of the operational event; every other failure is
 * reported via `Logger.errorWithOperationalEvent` exactly as before.
 */
export const reportSetItemError = ({ message, attributes, error }: SetItemErrorReport): void => {
	if (isQuotaExceededError(error) && fg('platform_bsc_filter_quota_exceeded_set_item_errors')) {
		Logger.error(message);
		return;
	}

	Logger.errorWithOperationalEvent({
		action: 'usedAtlBrowserStorageSetItemError',
		message,
		attributes,
	});
};
